import { DateTime } from 'luxon';
import { SERVICES_SITE_HOST } from '../constants/siteConfig';
import { buildOwnerSiteBaseUrl, resolveBrandRedirectHost } from './referralRedirectUrl';

/**
 * "Book a handover" from a car trip (spec 019 W27, T067): the car host's trip
 * page links to the services site with the car, the trip and a window one
 * hour each side of pickup. The services site reads the same parameters.
 * Pure, apart from reading the env for the services host.
 */

export const HANDOVER_PATH = '/services/book';

export interface HandoverParams {
  linkedCarId: string;
  linkedBookingId: string;
  /** yyyy-MM-dd in the car's zone. */
  date: string;
  /** HH:mm on 30-minute steps. */
  windowStart: string;
  windowEnd: string;
}

const TIME = /^([01]\d|2[0-3]):(00|30)$/;
const DAY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * One hour each side of pickup on 30-minute steps, inside the pickup day
 * (a window cannot cross midnight).
 */
export function handoverWindow(
  pickupMs: number,
  zone: string,
): Pick<HandoverParams, 'date' | 'windowStart' | 'windowEnd'> {
  const pickup = DateTime.fromMillis(pickupMs, { zone });
  const day = pickup.startOf('day');
  const minutes = pickup.hour * 60 + pickup.minute;
  let start = Math.floor((minutes - 60) / 30) * 30;
  let end = Math.ceil((minutes + 60) / 30) * 30;
  start = Math.max(0, start);
  end = Math.min(23 * 60 + 30, end);
  if (end <= start) end = Math.min(23 * 60 + 30, start + 30);
  if (end <= start) start = end - 30;
  const hhmm = (m: number) => day.plus({ minutes: m }).toFormat('HH:mm');
  return { date: day.toISODate()!, windowStart: hhmm(start), windowEnd: hhmm(end) };
}

export function handoverQuery(params: HandoverParams): URLSearchParams {
  const q = new URLSearchParams();
  q.set('linkedCarId', params.linkedCarId);
  q.set('linkedBookingId', params.linkedBookingId);
  q.set('date', params.date);
  q.set('windowStart', params.windowStart);
  q.set('windowEnd', params.windowEnd);
  q.set('serviceType', 'VEHICLE_HANDOVER');
  return q;
}

/** The services site's handover link for a car trip. */
export function handoverDeepLink(
  trip: { carId: string; bookingId: string; startAt: number; timezone: string },
  currentSiteHost: string,
  servicesHost: string = resolveBrandRedirectHost(SERVICES_SITE_HOST, currentSiteHost),
): string {
  const query = handoverQuery({
    linkedCarId: trip.carId,
    linkedBookingId: trip.bookingId,
    ...handoverWindow(trip.startAt, trip.timezone),
  });
  return `${buildOwnerSiteBaseUrl(servicesHost)}${HANDOVER_PATH}?${query.toString()}`;
}

/** The link's parameters on the services site, or null when one is missing or malformed. */
export function parseHandoverParams(params: URLSearchParams): HandoverParams | null {
  const linkedCarId = params.get('linkedCarId')?.trim() ?? '';
  const linkedBookingId = params.get('linkedBookingId')?.trim() ?? '';
  const date = params.get('date') ?? '';
  const windowStart = params.get('windowStart') ?? '';
  const windowEnd = params.get('windowEnd') ?? '';
  if (!linkedCarId || !linkedBookingId || !DAY.test(date)) return null;
  if (!TIME.test(windowStart) || !TIME.test(windowEnd) || windowEnd <= windowStart) return null;
  return { linkedCarId, linkedBookingId, date, windowStart, windowEnd };
}

/** A handover job that still stands (a cancelled, declined or expired one does not). */
const OPEN_JOB_STATUSES = ['BOOKED', 'REQUESTED', 'PENDING', 'COMPLETED'];

/** Whether the host can still book a handover for this trip: booked, not started, none yet. */
export function canBookHandover(
  trip: {
    status?: string | null;
    startAt: number;
    hostFinishedCheckin?: boolean | null;
    handoverJob?: { status: string } | null;
  },
  now = Date.now(),
): boolean {
  const job = trip.handoverJob;
  const jobOpen = Boolean(job && OPEN_JOB_STATUSES.includes(String(job.status).toUpperCase()));
  return (
    String(trip.status ?? '').toUpperCase() === 'BOOKED' &&
    !trip.hostFinishedCheckin &&
    trip.startAt > now &&
    !jobOpen
  );
}
